import firebase from "firebase";
import {deserialize, serialize} from "serializr";
import {CurrencyKindModel} from "@/modules/redactor/models/currency/CurrencyKindModel";

export default {
  namespaced: true,
  name: "redactor-store-currency-kind",
  state: {
    mRootPath: null,
    mGlobalUID: null,
    mConfigUID: null,
    mReadonly: false,
    mValues: []
  },

  getters: {
    getRootPath: (state) => () => {
      return state.mRootPath;
    },
    getGlobalUID: (state) => () => {
      return state.mGlobalUID;
    },
    getConfigUID: (state) => () => {
      return state.mConfigUID;
    },
    getReadonly: (state) => () => {
      return state.mReadonly;
    },
    getConfigPath: (state) => (global, subpath) => {
      let result = null;

      if (global === true) {
        result = state.mRootPath + state.mGlobalUID;
      } else if (global === false) {
        result = state.mRootPath + state.mConfigUID;
      } else {
        console.assert(false);
      }

      if (result && subpath) {
        result = result + subpath;
      }

      return result;
    },
    getValue: (state) => (uid) => {
      return state.mValues.find(v => v.uid === uid) || null;
    },
    getValueByKey: (state) => (key) => {
      return state.mValues.find(v => v.key === key) || null;
    },
    getModifierValues: (state) => () => {
      return state.mValues.filter(v => {
        const g = v.group;
        return typeof g === 'object' && g !== null && g.key === "modifier";
      });
    },
    getMapEnergyValues: (state) => () => {
      return state.mValues.filter(v => {
        const g = v.group;
        return typeof g === 'object' && g !== null && g.key === "map_energy";
      });
    },
    getValues: (state) => () => {
      return state.mValues;
    }
  },
  mutations: {
    setRootPath(state, value) {
      state.mRootPath = value;
    },
    setGlobalUID(state, value) {
      state.mGlobalUID = value;
    },
    setConfigUID(state, value) {
      state.mConfigUID = value;
    },
    setReadonly(state, readonly) {
      state.mReadonly = readonly;
    },

    setValues(state, values) {
      state.mValues = values;
    },
    addValues(state, values) {
      let keys = values.map(v => v.key);

      state.mValues = state.mValues.filter(v => {
        let index = keys.findIndex(key => {
          return key === v.key;
        });

        return index < 0;
      }).concat(values);
    },
    updateValues(state, values) {
      values.forEach(value => {
        let index = state.mValues.findIndex(v => {
          return v.key === value.key;
        });

        if (index >= 0) {
          state.mValues.splice(index, 1, value);
        } else {
          state.mValues.push(value);
        }
      });
    },
    updateValue(state, value) {
      value.changed = true;

      state.mValues.forEach(v => {
        v.changed = false;
      });

      let index = state.mValues.findIndex(v => {
        return v.uid === value.uid;
      });

      if (index >= 0) {
        state.mValues.splice(index, 1, value);
      } else {
        state.mValues.push(value);
      }
    },
    removeValue(state, value) {
      let index = state.mValues.findIndex(v => {
        return v.key === value.key;
      });

      if (index >= 0) {
        state.mValues.splice(index, 1);
      }
    },
    setValuesForScope(state, { values, global, shared }) {
      const isShared = shared || false;
      const uids = new Set(values.map(v => v.uid));

      state.mValues = state.mValues
        .filter(v => !(v.global === global && Boolean(v.sharedScope) === isShared))
        .filter(v => !uids.has(v.uid))
        .concat(values);
    }
  },
  actions: {
    init({commit}, payload) {
      console.info('init store-currency-kind');

      commit("setRootPath", payload.root_path);
      commit("setGlobalUID", payload.global_uid);
      commit("setConfigUID", payload.config_uid);
      commit("setReadonly", payload.readonly);
    },

    load({commit}, payload) {
      console.info('load store-currency-kind');

      if (!payload.config) {
        commit("setValuesForScope", { values: [], global: payload.global, shared: payload.shared || false });
        return;
      }

      const values = Object.values(payload.config);
      const items = values.map(v => deserialize(CurrencyKindModel, v));

      items.forEach(v => { v.global = payload.global; v.sharedScope = payload.shared || false; });

      commit("setValuesForScope", { values: items, global: payload.global, shared: payload.shared || false });
    },

    flush({dispatch}) {
      dispatch('pushValues');
    },

    clear({commit}) {
      commit("setValues", [])
    },

    validate({getters}) {
      let errors = [];

      const alreadySeenCode = new Set();

      const items = getters.getValues();

      items.filter(v => v.enabled).forEach(v => {
        try {
          if (!v.valid()) {
            errors.push({name: "currency-kind", key: v.uid});
          }
        } catch (error) {
          errors.push({name: "currency-kind", key: v.uid, error: error.message});
        }
      });

      items.forEach(v => {
        if (alreadySeenCode.has(v.code)) {
          errors.push({name: "currency-kind", key: v.uid, error: "Duplicated code: " + v.code});
        } else {
          alreadySeenCode.add(v.code);
        }
      })


      if (errors.length > 0) {
        return Promise.reject(errors);
      } else {
        return Promise.resolve();
      }
    },

    buildPatch({dispatch, getters}) {
      return dispatch("validate").then(() => {
        let result = {};
        let values = getters.getValues().filter(v => v.export);

        if (values.length > 0) {
          result["currency-kind"] = values.reduce((result, v) => {
            result[v.key] = serialize(CurrencyKindModel, v);
            return result;
          }, {});
        }

        return Promise.resolve(result);
      });
    },

    resetPatch({dispatch, getters}) {
      let values = getters.getValues().filter(v => v.export).map(v => {
        let value = v.clone();
        value.export = false;
        value.changed = false;
        return value;
      });

      if (values.length > 0) {
        return dispatch('pushValues', values);
      } else {
        return Promise.resolve();
      }
    },

    pushValue({ commit, getters, state }, value) {
      if (getters.getReadonly()) {
        return Promise.reject("The config is read-only");
      }

      if (value.global) {
        return Promise.reject("It is forbidden to modify a global record");
      }

      const basePath = getters.getConfigPath(false, "/currency-kind");
      const prev = state.mValues.find(v => v.uid === value.uid);
      const oldKey = prev?.key;
      const newKey = value.key;

      const refNew = firebase.database().ref(`${basePath}/${newKey}`);
      const refOld = oldKey && oldKey !== newKey
        ? firebase.database().ref(`${basePath}/${oldKey}`)
        : null;

      const cleanObject = (obj) => {
        return Object.fromEntries(
          Object.entries(obj).filter(([_, v]) => v !== undefined)
        );
      };

      return refNew.once('value').then(snapshot => {
        const prevData = snapshot.val() || {};
        const newData = cleanObject(serialize(CurrencyKindModel, value));
        const merged = Object.assign({}, prevData);

        Object.keys(prevData).forEach(key => {
          if (!(key in newData)) {
            delete merged[key];
          }
        });
        Object.assign(merged, newData);

        const save = () => refNew.set(merged, err => {
          if (err) {
            console.error("push failed:", err);
          } else {
            commit("updateValue", value);
          }
        });

        return refOld ? refOld.set(null).then(save) : save();
      });
    },

    pushValues({commit, getters}, values) {
      if (getters.getReadonly()) {
        return Promise.reject("The config is read-only");
      }

      let update = true;

      if (!values) {
        values = getters.getValues();
        update = false;
      }

      let data = values.filter(v => !v.global && !v.sharedScope).reduce((result, v) => {
        result[v.uid] = serialize(CurrencyKindModel, v);
        return result;
      }, {});

      let ref = firebase.database().ref(getters.getConfigPath(false, "/currency-kind"));

      if (update) {
        return ref.update(data, error => {
          if (error) {
            console.error("publish failed: " + error);
          } else if (update) {
            commit("updateValues", values);
          }
        });
      } else {
        return ref.set(data, error => {
          if (error) {
            console.error("publish failed: " + error);
          }
        });
      }
    },

    removeValue({commit, getters}, value) {
      if (getters.getReadonly()) {
        return Promise.reject("The config is read-only");
      } else if (value.global) {
        return Promise.reject("It is forbidden to delete a global record");
      }

      return firebase.database()
        .ref(getters.getConfigPath(false, "/currency-kind/" + value.key))
        .set(null, error => {
          if (error) {
            console.error("remove failed: " + error);
          } else {
            commit("removeValue", value);
          }
        });
    },

        removeBulkValues({commit, getters, rootGetters}, values) {
            if (getters.getReadonly()) {
                return Promise.reject("The config is read-only");
            }

            const globalValues = values.filter(v => v.global);
            if (globalValues.length > 0) {
                return Promise.reject("It is forbidden to delete global records");
            }

            const configUID = rootGetters["redactor-store/getConfigUID"]();
            const entityType = "currency-kind";
            const uids = values.map(v => v.uid);

            return Promise.all([
                firebase.auth().currentUser.getIdToken()
                    .then((token) => {
                        let requestOptions = {
                            method: "DELETE",
                            headers: { "Content-Type": "application/json", 'Authorization': token},
                            body: JSON.stringify({ uids })
                        };
                        return fetch(process.env.VUE_APP_FB_BACKEND_URL + `deleteConfigs/${configUID}/${entityType}`, requestOptions)
                            .then(async data => {
                                if (data.status != 200) {
                                    return data.text()
                                        .then((data)=>{
                                            throw data
                                        })
                                        .catch((error) => {
                                            throw error
                                        })
                                }else{
                                    // Remove all values from store after successful API call
                                    values.forEach(value => {
                                        commit("removeValue", value);
                                    });
                                }
                            })
                            .catch((error) => {
                                throw error
                            })
                    })
            ])
        },

    export({getters}) {
      return firebase.database().ref(getters.getConfigPath(false, "/currency-kind")).once('value')
        .then(snapshot => snapshot.val())
        .then(data => JSON.stringify(data, null, 2))
        .catch(error => {
          console.error("Export failed: ", error);
          return Promise.reject(error);
        });
    }
  }
}